import { useEffect, useState } from "react";
import { TrendingUp, Plus, Trash2, X, Users2, Eye, Target } from "lucide-react";
import { api } from "../lib/apiClient";
import { useApp, MONTHS } from "../lib/store";
import { PageHeader, StatCard, Empty } from "../components/Shared";
import { Panel } from "../components/Filters";
import { scoreColor } from "../lib/constants";
import { toast } from "sonner";

const MANAGERS = [{ id: "anjali", name: "Anjali" }, { id: "hani", name: "Hani" }];

export default function Growth() {
  const { month, year, can, role, selfEmployeeId } = useApp();
  const isManager = role === "manager";
  const [managerId, setManagerId] = useState(isManager ? selfEmployeeId : "anjali");
  const [rows, setRows] = useState([]);
  const [brands, setBrands] = useState([]);
  const [modal, setModal] = useState(null);

  const load = () => api.growth({ manager_id: managerId, month, year }).then(setRows).catch(() => {});
  useEffect(() => { load(); api.brands().then(setBrands).catch(() => {}); }, [managerId, month, year]);

  const avg = rows.length ? (rows.reduce((s, r) => s + (r.growth_score || 0), 0) / rows.length).toFixed(1) : "0";
  const totNew = rows.reduce((s, r) => s + (Number(r.new_followers) || 0), 0);
  const totReach = rows.reduce((s, r) => s + (Number(r.organic_reach) || 0), 0);

  return (
    <div>
      <PageHeader title="SMM Organic Growth & Reach" subtitle="Followers, reach & reels — feeds the 15-mark Organic Reach KRA automatically">
        {can.assignTasks && <button data-testid="add-growth-button" onClick={() => setModal({ brand: brands[0]?.name || "", month, year })} className="pill px-3 py-2 text-sm font-semibold brand-bg text-white"><Plus size={15} /> Add Metrics</button>}
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard label="Growth Score" value={`${avg}/15`} sub="avg across brands → KRA" icon={Target} accent="#10B981" />
        <StatCard label="New Followers" value={totNew.toLocaleString()} icon={Users2} accent="#2563EB" />
        <StatCard label="Organic Reach" value={totReach.toLocaleString()} icon={Eye} accent="#818CF8" />
        <StatCard label="Brands Tracked" value={rows.length} icon={TrendingUp} accent="#F59E0B" />
      </div>

      <div className="card-panel p-4 mb-5 flex flex-wrap gap-3 items-center">
        {!isManager && (
          <select value={managerId} onChange={(e) => setManagerId(e.target.value)} className="input-c" style={{ maxWidth: 200 }} data-testid="growth-manager-picker">
            {MANAGERS.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <span className="text-xs text-muted-c ml-auto">{MONTHS.find((m) => m.v === month)?.n} {year}</span>
      </div>

      <div className="card-panel overflow-x-auto" data-testid="growth-table">
        <table className="data-table">
          <thead><tr><th>Brand / Page</th><th>Opening</th><th>Closing</th><th>New</th><th>Target New</th><th>Organic Reach</th><th>Target Reach</th><th>Reel Views</th><th>Score /15</th>{can.assignTasks && <th></th>}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`growth-row-${r.id}`}>
                <td className="font-medium">{r.brand}</td>
                <td className="mono text-xs">{(r.opening_followers || 0).toLocaleString()}</td>
                <td className="mono text-xs">{(r.closing_followers || 0).toLocaleString()}</td>
                <td className="mono text-xs text-emerald-500">+{(r.new_followers || 0).toLocaleString()}</td>
                <td className="mono text-xs text-muted-c">{(r.target_new_followers || 0).toLocaleString()}</td>
                <td className="mono text-xs">{(r.organic_reach || 0).toLocaleString()}</td>
                <td className="mono text-xs text-muted-c">{(r.target_reach || 0).toLocaleString()}</td>
                <td className="mono text-xs">{(r.reel_views || 0).toLocaleString()}</td>
                <td className="mono font-bold" style={{ color: scoreColor((r.growth_score || 0) / 15 * 100) }}>{r.growth_score}</td>
                {can.assignTasks && <td className="flex gap-2"><button onClick={() => setModal(r)} className="text-muted-c hover:brand-text text-xs">Edit</button><button onClick={() => { if (window.confirm("Delete row?")) api.deleteGrowth(r.id).then(() => { toast.success("Deleted"); load(); }); }} className="text-muted-c hover:text-red-500"><Trash2 size={15} /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <Empty text="No growth metrics for this selection" />}
      </div>

      {modal && <GrowthModal row={modal} brands={brands} managerId={managerId} month={month} year={year} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function GrowthModal({ row, brands, managerId, month, year, onClose, onSaved }) {
  const editing = !!row.id;
  const [f, setF] = useState({
    brand: row.brand || brands[0]?.name || "", opening_followers: row.opening_followers || 0,
    closing_followers: row.closing_followers || 0, new_followers: row.new_followers || 0,
    target_new_followers: row.target_new_followers || 0, organic_reach: row.organic_reach || 0,
    target_reach: row.target_reach || 0, reel_views: row.reel_views || 0, shares: row.shares || 0, saves: row.saves || 0,
  });
  const set = (k, v) => setF({ ...f, [k]: k === "brand" ? v : Number(v) });
  const save = async () => {
    const body = { ...f, manager_id: managerId, month, year };
    if (editing) await api.updateGrowth(row.id, body); else await api.createGrowth(body);
    toast.success(editing ? "Metrics updated" : "Metrics added"); onSaved();
  };
  const F = ["opening_followers", "closing_followers", "new_followers", "target_new_followers", "organic_reach", "target_reach", "reel_views", "shares", "saves"];
  const LBL = { opening_followers: "Opening Followers", closing_followers: "Closing Followers", new_followers: "New Followers", target_new_followers: "Target New Followers", organic_reach: "Organic Reach", target_reach: "Target Reach", reel_views: "Reel Views", shares: "Shares", saves: "Saves" };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="card-panel w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="growth-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-subtle"><h3 className="font-display font-semibold">{editing ? "Edit" : "Add"} Growth Metrics</h3><button onClick={onClose}><X size={18} className="text-muted-c" /></button></div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">Brand</label>
            <select className="input-c" value={f.brand} onChange={(e) => set("brand", e.target.value)}>{brands.map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}</select></div>
          <div className="grid grid-cols-2 gap-3">
            {F.map((k) => <div key={k}><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">{LBL[k]}</label><input type="number" className="input-c mono" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`growth-${k}`} /></div>)}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-subtle"><button onClick={onClose} className="pill px-4 py-2 text-sm border-strong">Cancel</button><button onClick={save} className="pill px-4 py-2 text-sm brand-bg text-white font-semibold" data-testid="save-growth-button">{editing ? "Update" : "Add"}</button></div>
      </div>
    </div>
  );
}
